import { useState } from 'react';
import { useContext } from 'react';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { getItemProperties } from '../../../contexts/MixpanelHelper';
import user from '../../../helpers/user';
import { __ } from '../../../translations';
import { PinHolder } from '@intigral/shared';
import AlternativePaymentLinks from './AlternativePaymentLinks';
import { TVODOverlayType } from './TVODOverlay';
import { log } from '@intigral/utils';
import { tvodOneTimePurchaseApi } from '../../../services/serviceWrapper/tvodServiceWrapper';
import { QitafDiscountBreakdown } from './QitafDiscountBreakdown';
import { FlexWrapper, FontIcon, Text, Button } from '@intigral/shared';
import withPresetWidth from '@intigral/shared/components/hocs/withPresetWidth/withPresetWidth';
import { ppvOneTimePurchaseApi } from '../../../services/serviceWrapper/ppvServiceWrapper';
import AlternativePaymentLinksPPV from './AlternativePaymentLinksPPV';
import { EVENT } from '../../../contexts/MixpanelConstants';
import { useToggleWatchlistItem } from '../../../hooks/watchlist/useToggleWatchlist';

interface tvodInfo {
    priceWithVat: number;
    currency: string;
    productId: string;
}

interface InputPINPropsType {
    tvodInfo?: tvodInfo;
    setCurrentComponentType: (e: any) => void;
    content?: any;
    isPPV?: boolean;
    eventData?: any;
    handlePurchase: (e: any) => void;
    onClose: () => void;
    qitafUpdatedPrice?: number;
    qitafApplied?: boolean;
}

export const InputPIN = ({
    tvodInfo,
    setCurrentComponentType,
    content,
    isPPV,
    eventData,
    handlePurchase,
    onClose,
    qitafUpdatedPrice,
    qitafApplied = false,
}: InputPINPropsType) => {
    const username = user.getMSISDN() || user.getUsername();
    const durationInHours = 48;
    const { priceWithVat, currency, productId } = tvodInfo;
    const toggleWatchlistItemMutation = useToggleWatchlistItem();

    const displayPrice =
        qitafApplied && qitafUpdatedPrice != null ? Number(qitafUpdatedPrice).toFixed(2) : priceWithVat;

    let tvodDescriptionOneText = __('tvod_description_one') || '';
    let tvodDescriptionTwoText = __('tvod_description_two') || '';

    tvodDescriptionOneText = tvodDescriptionOneText.replace('{username}', username);
    tvodDescriptionOneText = tvodDescriptionOneText.replace('{priceWithVat}', displayPrice);
    tvodDescriptionOneText = tvodDescriptionOneText.replace('{currency}', currency);
    tvodDescriptionTwoText = tvodDescriptionTwoText.replace('{durationInHours}', durationInHours);

    let tvodButtonText = __('tvod_pay_button') || '';
    tvodButtonText = tvodButtonText.replace('{priceWithVat}', priceWithVat);
    tvodButtonText = tvodButtonText.replace('{currency}', currency);
    tvodButtonText = tvodButtonText.replace('{durationInHours}', durationInHours);

    const [apiProcessing, setApiProcessing] = useState(false);
    const [pin, setPin] = useState('');
    const PIN_LENGTH = 4;

    const { track } = useContext(MixpanelContext);

    const onPaySelected = async () => {
        setApiProcessing(true);
        if (isPPV) {
            track(EVENT.PPV.PPV_PURCHASED_STARTED, {
                'Event Title': eventData?.name,
                'Event ID': eventData?.eventId,
                'Purchase Method': 'STCH',
            });
            try {
                const oneTimePurchase = await ppvOneTimePurchaseApi({ eventData, pin, handlePurchase });
                if (oneTimePurchase === 'accepted') {
                    onClose();
                    track(EVENT.PPV.PPV_PURCHASED_COMPLETED, {
                        'Event Title': eventData?.name,
                        'Event ID': eventData?.eventId,
                        'Purchase Method': 'STCH',
                        Platform: 'Web',
                    });
                    return false;
                }
            } catch (e) {
                track(EVENT.PPV.PPV_PURCHASED_FAILED, {
                    'Failure Reason': JSON.stringify(e),
                });
            }
        } else {
            track(`Tvod - Purchase - Attempt`, {});
            try {
                const oneTimePurchase = await tvodOneTimePurchaseApi({ productId, pin, currency }, handlePurchase);
                if (oneTimePurchase) {
                    setCurrentComponentType(TVODOverlayType.SUCCESS);
                    if (content?.Id) {
                        toggleWatchlistItemMutation.mutate({
                            itemId: content.Id,
                            category: (content?.category?.toLowerCase() ?? '') as 'linear' | 'vod',
                            isAdded: false,
                        });
                    }
                    track(`TVOD - Purchase - Success`, getItemProperties(content));
                    return false;
                }
            } catch (e) {
                track(`TVOD - Purchase - Failed`, {
                    ...getItemProperties(content),
                    'Failure Reason': JSON.stringify(e),
                });
            }
        }
        setApiProcessing(false);
    };

    const FlexWidth = withPresetWidth(FlexWrapper);

    return (
        <>
            <FlexWrapper flexDirection="column" gap={{ default: '2x', md: '3x' }}>
                <FontIcon name="cart" size="large" md="size60" color="green" />
                <Text as="h2" variant="h2" margin="0">
                    {isPPV ? __('tvod_ppv_rent_now') : __('tvod_rent_now')}
                </Text>

                {qitafApplied && qitafUpdatedPrice != null && qitafUpdatedPrice !== priceWithVat && (
                    <QitafDiscountBreakdown priceWithVat={priceWithVat} updatedPrice={qitafUpdatedPrice} />
                )}

                <Text as="p" margin="0" color="gray" size="14">
                    <span dangerouslySetInnerHTML={{ __html: tvodDescriptionOneText }} />
                </Text>
                <Text as="p" margin="0" color="gray" size="14">
                    <span dangerouslySetInnerHTML={{ __html: tvodDescriptionTwoText }} />
                </Text>
                <div>
                    <Text as="h4" variant="h4" margin="0">
                        {__('tvod_enter_account_pin')}
                    </Text>
                    <PinHolder
                        value={pin}
                        renderInput={(props) => <input {...props} />}
                        onChange={(updatePin) => {
                            setPin(updatePin);
                        }}
                        skipDefaultStyles
                    />
                </div>
                <FlexWidth flexDirection="column" alignItems="center" presetWidth="medium" paddingTop="2x">
                    <Button
                        onClick={onPaySelected}
                        disabled={pin.length !== PIN_LENGTH}
                        // {...(apiProcessing && { loading: true })}
                        loading={apiProcessing}
                    >
                        {tvodButtonText}{' '}
                        {qitafApplied && qitafUpdatedPrice != null ? Number(qitafUpdatedPrice).toFixed(2) : ''}{' '}
                        {qitafApplied ? __('qitaf_currency') : ''}
                    </Button>
                </FlexWidth>
                {isPPV ? (
                    <AlternativePaymentLinksPPV
                        onSelected={setCurrentComponentType}
                        currentOverlayType={TVODOverlayType.PIN}
                    />
                ) : (
                    <AlternativePaymentLinks
                        onSelected={setCurrentComponentType}
                        currentOverlayType={TVODOverlayType.PIN}
                    />
                )}
            </FlexWrapper>
        </>
    );
};
